import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, MoonStar, LogOut, LayoutDashboard, CalendarHeart, Settings2, Bell, Mail, Star, ChevronDown, Volume2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { AmbientAudio, getMusicPref, setMusicPref } from "./AmbientAudio";
import { ReviewPrompt } from "./ReviewPrompt";
import { ReviewGate } from "./ReviewGate";
import { QuotaGate } from "./QuotaGate";
import { initPush, isNativeApp, PUSH_SOUNDS, getPushSound, savePushSound, previewPushSound, getPushEnabled, savePushEnabled } from "../lib/push";
import { isNativeAndroid } from "../lib/billing";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { ORACOLI_MENU } from "../lib/methods";
import CompletaProfilo from "./CompletaProfilo";
import { MoonLogo } from "./MoonLogo";

const allLinks = [
  { to: "/", label: "Home" },
  { to: "/chi-sono", label: "Chi Sono" },
  { to: "/oltre-le-carte", label: "Oltre le Carte" },
  { to: "/piani", label: "Piani" },
];

// Sul sito il menu non mostra «Piani»: acquisti e abbonamenti avvengono solo nell'app Android.
// La pagina /piani resta raggiungibile come pagina informativa (senza prezzi né acquisti).
const links = isNativeAndroid() ? allLinks : allLinks.filter((l) => l.to !== "/piani");

// Il menu «Oracoli e carte» deriva da ORACOLI_MENU (lib/methods.js):
// un nuovo metodo aggiunto lì compare automaticamente qui, desktop e mobile.
const oracoli = ORACOLI_MENU;

const navClass = ({ isActive }) =>
  `text-[12px] tracking-wide whitespace-nowrap transition-colors duration-300 ${
    isActive ? "text-amber-400" : "text-purple-200/70 hover:text-amber-200"
  }`;

export const Layout = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [musicOn, setMusicOn] = useState(getMusicPref());
  const [unread, setUnread] = useState(0);
  const [adminNotifs, setAdminNotifs] = useState({ virginia: 0, reviews: 0, total: 0 });
  const [adminMenu, setAdminMenu] = useState(false);
  const [oracoliOpen, setOracoliOpen] = useState(false);
  const [pushSound, setPushSound] = useState("default");
  const [pushEnabled, setPushEnabled] = useState(true);
  const navigate = useNavigate();

  // scelta del suono del Verdetto (solo app Android): caricata all'apertura delle impostazioni
  useEffect(() => {
    if (showSettings && isNativeApp() && user) {
      getPushSound().then(setPushSound);
      getPushEnabled().then(setPushEnabled);
    }
  }, [showSettings, user]);

  const selectPushSound = (id) => {
    setPushSound(id);
    previewPushSound(id);
    savePushSound(id).catch(() => {});
  };

  // Push native (solo Verdetto Settimanale): registrazione token + apertura su /memoria al tap
  useEffect(() => {
    if (user) initPush((route) => navigate(route));
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      return;
    }
    let stop = false;
    const load = () => api.get("/notifications/unread-count").then((r) => { if (!stop) setUnread(r.data.unread || 0); }).catch(() => {});
    const onSeen = () => load();
    window.addEventListener("arcana-notifications-seen", onSeen);
    load();
    const t = setInterval(load, 60000);
    return () => { stop = true; clearInterval(t); window.removeEventListener("arcana-notifications-seen", onSeen); };
  }, [user]);

  useEffect(() => {
    if (!user || user.role !== "admin") {
      setAdminNotifs({ virginia: 0, reviews: 0, total: 0 });
      return;
    }
    let stop = false;
    const load = () => api.get("/admin/notifications").then((r) => { if (!stop) setAdminNotifs(r.data); }).catch(() => {});
    const onAnswered = () => load();
    window.addEventListener("arcana-virginia-answered", onAnswered);
    load();
    const t = setInterval(load, 60000);
    return () => { stop = true; clearInterval(t); window.removeEventListener("arcana-virginia-answered", onAnswered); };
  }, [user]);

  useEffect(() => {
    const onPref = (e) => setMusicOn(e.detail);
    window.addEventListener("arcana-music", onPref);
    return () => window.removeEventListener("arcana-music", onPref);
  }, []);

  const toggleMusic = () => setMusicPref(!getMusicPref());

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#0f0617]">
      <AmbientAudio />
      <header
        className="fixed top-0 left-0 right-0 z-50 bg-[#0f0617]/90 backdrop-blur-xl border-b border-amber-500/15"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-2">
            <MoonLogo className="w-6 h-6 rounded-full" />
            <span className="flex flex-col leading-none">
              <img src="/arcana-wordmark.png" alt="ARCANA" className="h-3 sm:h-3.5 w-auto self-start" />
              <span className="text-[8px] sm:text-[9px] tracking-[0.14em] uppercase text-amber-300/60 mt-1">
                Antica cartomanzia &amp; Oracoli
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-3.5" data-testid="desktop-nav">
            <NavLink to="/" end className={navClass} data-testid="nav-home-link">Home</NavLink>
            {user && (
              <NavLink to="/crediti" className={navClass} data-testid="nav-crediti-link">Storico dei crediti</NavLink>
            )}
            <div className="relative">
              <button
                onClick={() => setOracoliOpen(!oracoliOpen)}
                data-testid="nav-oracoli-btn"
                className="text-[12px] tracking-wide whitespace-nowrap transition-colors duration-300 text-purple-200/70 hover:text-amber-200 flex items-center gap-1"
              >
                Oracoli e carte <ChevronDown className={`w-3 h-3 transition-transform ${oracoliOpen ? "rotate-180" : ""}`} />
              </button>
              {oracoliOpen && (
                <div className="absolute left-0 top-7 z-50 w-48 mystic-card p-2 max-h-72 overflow-y-auto" data-testid="nav-oracoli-menu">
                  {oracoli.map((o) => (
                    <NavLink
                      key={o.to}
                      to={o.to}
                      onClick={() => setOracoliOpen(false)}
                      className="block px-3 py-2 text-xs text-purple-100/85 hover:text-amber-200 rounded-lg hover:bg-amber-500/10 transition-colors"
                      data-testid={`nav-oracoli-${o.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {o.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
            {links.slice(1).map((l) => (
              <NavLink key={l.to} to={l.to} className={navClass} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                {user.role === "admin" && (
                  <div className="relative">
                    <button
                      onClick={() => setAdminMenu(!adminMenu)}
                      data-testid="admin-inbox-btn"
                      title="Notifiche amministratore"
                      aria-label="Notifiche amministratore"
                      className="relative text-purple-300/70 hover:text-amber-300 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      {adminNotifs.total > 0 && (
                        <span data-testid="admin-inbox-badge" className="absolute -top-1.5 -right-1.5 bg-amber-500 text-[#0f0617] text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                          {adminNotifs.total}
                        </span>
                      )}
                    </button>
                    {adminMenu && (
                      <div className="absolute right-0 top-8 z-50 w-64 mystic-card p-2 space-y-1" data-testid="admin-inbox-menu">
                        <button
                          onClick={() => { setAdminMenu(false); navigate("/admin?tab=virginia"); }}
                          data-testid="admin-inbox-goto-virginia"
                          className="w-full text-left text-xs text-purple-100/85 hover:text-amber-200 rounded-lg px-3 py-2 hover:bg-amber-500/10 transition-colors flex items-center justify-between"
                        >
                          <span>Domande per Virginia</span>
                          {adminNotifs.virginia > 0 && <span className="bg-amber-500 text-[#0f0617] text-[10px] font-bold rounded-full px-2 py-0.5">{adminNotifs.virginia}</span>}
                        </button>
                        <button
                          onClick={() => { setAdminMenu(false); navigate("/admin?tab=recensioni"); }}
                          data-testid="admin-inbox-goto-reviews"
                          className="w-full text-left text-xs text-purple-100/85 hover:text-amber-200 rounded-lg px-3 py-2 hover:bg-amber-500/10 transition-colors flex items-center justify-between"
                        >
                          <span>Recensioni da approvare</span>
                          {adminNotifs.reviews > 0 && <span className="bg-amber-500 text-[#0f0617] text-[10px] font-bold rounded-full px-2 py-0.5">{adminNotifs.reviews}</span>}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <Link
                  to={user.role === "admin" ? "/admin" : "/cliente"}
                  data-testid="header-area-link"
                  className="text-sm text-purple-200/80 hover:text-amber-200 flex items-center gap-1.5 transition-colors"
                >
                  {user.role === "admin" ? <LayoutDashboard className="w-4 h-4" /> : <CalendarHeart className="w-4 h-4" />}
                  {user.role === "admin" ? "Area Admin" : "Area Cliente"}
                </Link>
                <button onClick={handleLogout} data-testid="header-logout-btn" className="text-purple-300/60 hover:text-red-300 transition-colors">
                  <LogOut className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate("/notifiche")}
                  data-testid="notifiche-btn"
                  title="Notifiche"
                  aria-label="Notifiche"
                  className="relative text-purple-300/70 hover:text-amber-300 transition-colors"
                >
                  <Bell className="w-4 h-4" />
                  {unread > 0 && (
                    <span data-testid="notifiche-badge" className="absolute -top-1.5 -right-1.5 bg-amber-500 text-[#0f0617] text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                      {unread}
                    </span>
                  )}
                </button>
              </>
            ) : (
              <Link to="/accedi" data-testid="header-login-link" className="text-sm text-purple-200/80 hover:text-amber-200 transition-colors">
                Accedi
              </Link>
            )}
            <button onClick={() => setShowSettings(true)} data-testid="header-settings-btn" className="text-purple-300/70 hover:text-amber-300 transition-colors" aria-label="Impostazioni">
              <Settings2 className="w-4 h-4" />
            </button>
          </div>

      {showSettings && (
        <div className="fixed inset-0 z-[60]" onClick={() => setShowSettings(false)} data-testid="settings-overlay">
          <div className="absolute top-16 right-4 mystic-card p-5 w-72" onClick={(e) => e.stopPropagation()} data-testid="settings-panel">
            <p className="eyebrow mb-4">Impostazioni</p>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm text-amber-100">Musica di sottofondo</p>
                <p className="text-[10px] text-purple-300/50 mt-0.5 leading-relaxed">Atmosfera delicata durante la navigazione</p>
              </div>
              <button
                onClick={toggleMusic}
                data-testid="music-toggle"
                aria-label="Attiva o disattiva la musica di sottofondo"
                className={`w-11 h-6 rounded-full border transition-colors shrink-0 relative ${musicOn ? "bg-amber-500/30 border-amber-500/60" : "bg-[#180a26] border-[#4a2175]"}`}
              >
                <span className={`absolute top-0.5 w-[18px] h-[18px] rounded-full bg-amber-300 transition-all ${musicOn ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>
            {isNativeApp() && user && (
              <div className="mt-5 pt-4 border-t border-amber-500/15">
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div>
                    <p className="text-sm text-amber-100">Notifiche push</p>
                    <p className="text-[10px] text-purple-300/50 mt-0.5 leading-relaxed">Avvisi di Arcana sul telefono (Verdetto e regali)</p>
                  </div>
                  <button
                    onClick={async () => {
                      const next = !pushEnabled;
                      setPushEnabled(next);
                      try { await savePushEnabled(next); } catch { setPushEnabled(!next); }
                    }}
                    data-testid="push-enabled-toggle"
                    aria-label="Attiva o disattiva le notifiche push"
                    className={`w-11 h-6 rounded-full border transition-colors shrink-0 relative ${pushEnabled ? "bg-amber-500/30 border-amber-500/60" : "bg-[#180a26] border-[#4a2175]"}`}
                  >
                    <span className={`absolute top-0.5 w-[18px] h-[18px] rounded-full bg-amber-300 transition-all ${pushEnabled ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </div>
                <p className="text-sm text-amber-100">Suono del Verdetto Settimanale</p>
                <p className="text-[10px] text-purple-300/50 mt-0.5 leading-relaxed">Il suono della notifica del Verdetto sul telefono</p>
                <div className="mt-3 space-y-1.5" data-testid="push-sound-picker">
                  {PUSH_SOUNDS.map((s) => (
                    <button
                      key={s.id}
                      data-testid={`push-sound-${s.id}`}
                      onClick={() => selectPushSound(s.id)}
                      className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 text-xs transition-colors ${
                        pushSound === s.id
                          ? "border-amber-500/60 bg-amber-500/10 text-amber-200"
                          : "border-[#4a2175] text-purple-200/70 hover:border-amber-500/30"
                      }`}
                    >
                      <span>{s.label}</span>
                      <Volume2 className="w-3.5 h-3.5 opacity-60" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden text-amber-200 p-3 -m-3" data-testid="mobile-menu-btn" aria-label="Apri il menu">
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#180a26] border-l border-amber-500/20 w-72">
              <div className="flex flex-col gap-6 mt-10">
                <div className="flex items-center justify-between gap-3 pb-5 border-b border-amber-500/15">
                  <p className="text-sm text-amber-100">Musica di sottofondo</p>
                  <button
                    onClick={toggleMusic}
                    data-testid="music-toggle-mobile"
                    aria-label="Attiva o disattiva la musica di sottofondo"
                    className={`w-11 h-6 rounded-full border transition-colors shrink-0 relative ${musicOn ? "bg-amber-500/30 border-amber-500/60" : "bg-[#180a26] border-[#4a2175]"}`}
                  >
                    <span className={`absolute top-0.5 w-[18px] h-[18px] rounded-full bg-amber-300 transition-all ${musicOn ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </div>
                <NavLink to="/" end onClick={() => setOpen(false)} className={navClass} data-testid="mobile-nav-home-link">
                  Home
                </NavLink>
                {user && (
                  <NavLink to="/crediti" onClick={() => setOpen(false)} className={navClass} data-testid="mobile-nav-crediti-link">
                    Storico dei crediti
                  </NavLink>
                )}
                <div>
                  <button
                    onClick={() => setOracoliOpen(!oracoliOpen)}
                    data-testid="mobile-nav-oracoli-btn"
                    className="text-[12px] tracking-wide text-purple-200/70 hover:text-amber-200 flex items-center gap-1.5 transition-colors"
                  >
                    Oracoli e carte <ChevronDown className={`w-3.5 h-3.5 transition-transform ${oracoliOpen ? "rotate-180" : ""}`} />
                  </button>
                  {oracoliOpen && (
                    <div className="flex flex-col gap-4 pl-4 mt-4 max-h-56 overflow-y-auto" data-testid="mobile-nav-oracoli-menu">
                      {oracoli.map((o) => (
                        <NavLink
                          key={o.to}
                          to={o.to}
                          onClick={() => setOpen(false)}
                          className={navClass}
                          data-testid={`mobile-nav-oracoli-${o.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {o.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
                {links.slice(1).map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={navClass}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  >
                    {l.label}
                  </NavLink>
                ))}
                <div className="h-px bg-amber-500/15" />
                {user ? (
                  <>
                    <Link
                      to={user.role === "admin" ? "/admin" : "/cliente"}
                      onClick={() => setOpen(false)}
                      data-testid="mobile-area-link"
                      className="text-sm text-purple-200/80"
                    >
                      {user.role === "admin" ? "Area Admin" : "Area Cliente"}
                    </Link>
                    <button onClick={handleLogout} data-testid="mobile-logout-btn" className="text-left text-sm text-red-300/80">
                      Esci
                    </button>
                    {user.role === "admin" && (
                      <>
                        <button
                          onClick={() => { setOpen(false); navigate("/admin?tab=virginia"); }}
                          data-testid="mobile-admin-inbox"
                          className="text-left text-sm text-purple-200/80 flex items-center gap-2"
                        >
                          <Mail className="w-4 h-4" />
                          Domande per Virginia
                          {adminNotifs.virginia > 0 && (
                            <span data-testid="mobile-admin-inbox-badge" className="bg-amber-500 text-[#0f0617] text-[10px] font-bold rounded-full px-2 py-0.5">{adminNotifs.virginia}</span>
                          )}
                        </button>
                        <button
                          onClick={() => { setOpen(false); navigate("/admin?tab=recensioni"); }}
                          data-testid="mobile-admin-reviews"
                          className="text-left text-sm text-purple-200/80 flex items-center gap-2"
                        >
                          <Star className="w-4 h-4" />
                          Recensioni da approvare
                          {adminNotifs.reviews > 0 && (
                            <span data-testid="mobile-admin-reviews-badge" className="bg-amber-500 text-[#0f0617] text-[10px] font-bold rounded-full px-2 py-0.5">{adminNotifs.reviews}</span>
                          )}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => { setOpen(false); navigate("/notifiche"); }}
                      data-testid="mobile-notifiche-btn"
                      className="text-left text-sm text-purple-200/80 flex items-center gap-2"
                    >
                      <Bell className="w-4 h-4" />
                      Notifiche
                      {unread > 0 && (
                        <span data-testid="mobile-notifiche-badge" className="bg-amber-500 text-[#0f0617] text-[10px] font-bold rounded-full px-2 py-0.5">{unread}</span>
                      )}
                    </button>
                  </>
                ) : (
                  <Link to="/accedi" onClick={() => setOpen(false)} data-testid="mobile-login-link" className="text-sm text-purple-200/80">
                    Accedi
                  </Link>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="flex-1" style={{ paddingTop: "calc(57px + env(safe-area-inset-top))" }}>
        <Outlet />
      </main>

      <ReviewPrompt />
      <ReviewGate />
      <QuotaGate />
      <CompletaProfilo />

      <footer className="border-t border-amber-500/15 bg-[#0c0513] py-10 mt-10" data-testid="site-footer">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 text-center space-y-3">
          <div className="flex items-center justify-center gap-2">
            <MoonLogo className="w-4 h-4 rounded-full" />
            <img src="/arcana-wordmark.png" alt="ARCANA" className="h-4 w-auto" />
          </div>
          <p className="text-xs text-amber-300/60 tracking-[0.2em] uppercase">Antica cartomanzia &amp; Oracoli</p>
          <p className="text-xs text-purple-300/50">Consulenze di cartomanzia e letture con le carte, con cura e riservatezza.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-xs px-2" data-testid="footer-legal-links">
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-purple-300/60 hover:text-amber-300 transition-colors">Privacy Policy</Link>
            <span className="text-purple-300/30">·</span>
            <Link to="/termini" data-testid="footer-termini-link" className="text-purple-300/60 hover:text-amber-300 transition-colors">Termini e Condizioni</Link>
            <span className="text-purple-300/30">·</span>
            <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="text-purple-300/60 hover:text-amber-300 transition-colors">Disclaimer</Link>
            <span className="text-purple-300/30">·</span>
            <Link to="/elimina-account" data-testid="footer-elimina-link" className="text-purple-300/60 hover:text-amber-300 transition-colors">Elimina account</Link>
            <span className="text-purple-300/30">·</span>
            <Link to="/segnala" data-testid="footer-segnala-link" className="text-purple-300/60 hover:text-amber-300 transition-colors">Segnala un problema</Link>
          </div>
          <p className="text-xs text-purple-300/40">© 2026 ARCANA. Tutti i diritti riservati.</p>
        </div>
      </footer>
    </div>
  );
};
